import { useEffect, useRef } from "react";
import {
  PORTRAIT_GRID_HEIGHT,
  PORTRAIT_GRID_WIDTH,
  PORTRAIT_POINTS,
} from "../data/portraitPoints";

export const PARTICLE_GAP = 5;
export const MOBILE_PARTICLE_GAP = 8;
export const PARTICLE_SIZE = 1.35;
export const MOUSE_RADIUS = 92;
export const REPULSION_FORCE = 0.68;
export const RETURN_SPEED = 0.055;
export const PARTICLE_OPACITY = 0.72;
export const BRIGHTNESS_THRESHOLD = 238;

const MOBILE_BREAKPOINT = 768;
const MAX_DEVICE_PIXEL_RATIO = 2;
const VELOCITY_DAMPING = 0.82;
const FRAME_DURATION = 1000 / 60;

export const PARTICLE_CONFIG = Object.freeze({
  gap: PARTICLE_GAP,
  mobileGap: MOBILE_PARTICLE_GAP,
  size: PARTICLE_SIZE,
  mouseRadius: MOUSE_RADIUS,
  repulsionForce: REPULSION_FORCE,
  returnSpeed: RETURN_SPEED,
  opacity: PARTICLE_OPACITY,
  brightnessThreshold: BRIGHTNESS_THRESHOLD,
  mobileBreakpoint: MOBILE_BREAKPOINT,
  maxDevicePixelRatio: MAX_DEVICE_PIXEL_RATIO,
  velocityDamping: VELOCITY_DAMPING,
});

const EMPTY_CONFIG = Object.freeze({});

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function finiteNumber(value, fallback, minimum, maximum) {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return clamp(value, minimum, maximum);
}

function coordinateNoise(x, y) {
  let value = Math.imul(x + 1, 374761393) ^ Math.imul(y + 1, 668265263);
  value = Math.imul(value ^ (value >>> 13), 1274126177);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
}

function addMediaQueryListener(mediaQuery, listener) {
  if (!mediaQuery) return () => {};

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }

  mediaQuery.addListener(listener);
  return () => mediaQuery.removeListener(listener);
}

/**
 * Draws the portrait from precomputed samples, so no source image is loaded
 * or retained by the application at runtime.
 */
export default function ParticlePortrait({
  className,
  config = EMPTY_CONFIG,
  style,
}) {
  const canvasRef = useRef(null);

  const gap = finiteNumber(config.gap, PARTICLE_GAP, 2, 24);
  const mobileGap = finiteNumber(
    config.mobileGap,
    MOBILE_PARTICLE_GAP,
    gap,
    32,
  );
  const particleSize = finiteNumber(config.size, PARTICLE_SIZE, 0.25, 8);
  const mouseRadius = finiteNumber(config.mouseRadius, MOUSE_RADIUS, 1, 500);
  const repulsionForce = finiteNumber(
    config.repulsionForce,
    REPULSION_FORCE,
    0,
    4,
  );
  const returnSpeed = finiteNumber(
    config.returnSpeed,
    RETURN_SPEED,
    0.001,
    0.5,
  );
  const opacity = finiteNumber(config.opacity, PARTICLE_OPACITY, 0, 1);
  const brightnessThreshold = finiteNumber(
    config.brightnessThreshold,
    BRIGHTNESS_THRESHOLD,
    0,
    255,
  );
  const mobileBreakpoint = finiteNumber(
    config.mobileBreakpoint,
    MOBILE_BREAKPOINT,
    240,
    2000,
  );
  const maxDevicePixelRatio = finiteNumber(
    config.maxDevicePixelRatio,
    MAX_DEVICE_PIXEL_RATIO,
    1,
    4,
  );
  const velocityDamping = finiteNumber(
    config.velocityDamping,
    VELOCITY_DAMPING,
    0.1,
    0.99,
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return undefined;

    const reducedMotionQuery = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    const finePointerQuery = window.matchMedia?.(
      "(hover: hover) and (pointer: fine)",
    );
    const coarsePointerQuery = window.matchMedia?.("(pointer: coarse)");

    let reducedMotion = reducedMotionQuery?.matches ?? false;
    let finePointer = finePointerQuery?.matches ?? false;
    let coarsePointer = coarsePointerQuery?.matches ?? false;
    let particles = [];
    let width = 0;
    let height = 0;
    let animationFrame = 0;
    let resizeFrame = 0;
    let lastFrameTime = 0;
    let disposed = false;

    const pointer = { x: 0, y: 0, active: false };

    const clearCanvas = () => {
      context.clearRect(0, 0, width, height);
    };

    const drawParticles = () => {
      clearCanvas();
      context.fillStyle = "#111111";

      for (const particle of particles) {
        context.globalAlpha = particle.opacity;
        context.fillRect(
          particle.x - particle.size / 2,
          particle.y - particle.size / 2,
          particle.size,
          particle.size,
        );
      }

      context.globalAlpha = 1;
    };

    const resetParticles = () => {
      for (const particle of particles) {
        particle.x = particle.homeX;
        particle.y = particle.homeY;
        particle.velocityX = 0;
        particle.velocityY = 0;
      }
    };

    const stopAnimation = () => {
      if (animationFrame !== 0) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      lastFrameTime = 0;
    };

    const animate = (time) => {
      animationFrame = 0;
      if (disposed || reducedMotion) return;

      const frameScale = lastFrameTime
        ? clamp((time - lastFrameTime) / FRAME_DURATION, 0.5, 2)
        : 1;
      const damping = Math.pow(velocityDamping, frameScale);
      const radiusSquared = mouseRadius * mouseRadius;
      let particlesAreMoving = false;
      lastFrameTime = time;

      for (const particle of particles) {
        if (pointer.active) {
          const deltaX = particle.x - pointer.x;
          const deltaY = particle.y - pointer.y;
          const distanceSquared = deltaX * deltaX + deltaY * deltaY;

          if (distanceSquared < radiusSquared) {
            const distance = Math.sqrt(distanceSquared);
            const falloff = 1 - distance / mouseRadius;
            const force = falloff * falloff * repulsionForce * frameScale;

            if (distance > 0.001) {
              particle.velocityX += (deltaX / distance) * force;
              particle.velocityY += (deltaY / distance) * force;
            } else {
              const angle =
                coordinateNoise(
                  Math.round(particle.homeX),
                  Math.round(particle.homeY),
                ) *
                Math.PI *
                2;
              particle.velocityX += Math.cos(angle) * force;
              particle.velocityY += Math.sin(angle) * force;
            }
          }
        }

        particle.velocityX +=
          (particle.homeX - particle.x) * returnSpeed * frameScale;
        particle.velocityY +=
          (particle.homeY - particle.y) * returnSpeed * frameScale;
        particle.velocityX *= damping;
        particle.velocityY *= damping;
        particle.x += particle.velocityX * frameScale;
        particle.y += particle.velocityY * frameScale;

        const isDisplaced =
          Math.abs(particle.x - particle.homeX) > 0.04 ||
          Math.abs(particle.y - particle.homeY) > 0.04;
        const hasVelocity =
          Math.abs(particle.velocityX) > 0.025 ||
          Math.abs(particle.velocityY) > 0.025;

        if (hasVelocity || (!pointer.active && isDisplaced)) {
          particlesAreMoving = true;
        }
      }

      drawParticles();
      if (particlesAreMoving) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        lastFrameTime = 0;
      }
    };

    const requestAnimation = () => {
      if (
        animationFrame === 0 &&
        !document.hidden &&
        !reducedMotion &&
        particles.length > 0
      ) {
        lastFrameTime = 0;
        animationFrame = requestAnimationFrame(animate);
      }
    };

    const rebuildParticles = () => {
      resizeFrame = 0;
      if (disposed) return;

      const bounds = canvas.getBoundingClientRect();
      const nextWidth = Math.round(bounds.width);
      const nextHeight = Math.round(bounds.height);
      if (nextWidth <= 0 || nextHeight <= 0) return;

      pointer.active = false;
      stopAnimation();
      width = nextWidth;
      height = nextHeight;

      const devicePixelRatio = Math.min(
        Math.max(window.devicePixelRatio || 1, 1),
        maxDevicePixelRatio,
      );
      canvas.width = Math.round(width * devicePixelRatio);
      canvas.height = Math.round(height * devicePixelRatio);
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const scale = Math.min(
        width / PORTRAIT_GRID_WIDTH,
        height / PORTRAIT_GRID_HEIGHT,
      );
      const portraitWidth = PORTRAIT_GRID_WIDTH * scale;
      const portraitHeight = PORTRAIT_GRID_HEIGHT * scale;
      const offsetX = (width - portraitWidth) / 2;
      const offsetY = (height - portraitHeight) / 2;
      const desiredGap =
        width < mobileBreakpoint || coarsePointer ? mobileGap : gap;
      const sampleStep = Math.max(1, Math.round(desiredGap / scale));
      const nextParticles = [];

      for (const [sourceX, sourceY, brightness] of PORTRAIT_POINTS) {
        if (brightness > brightnessThreshold) continue;
        if (
          sampleStep > 1 &&
          (sourceX % sampleStep !== 0 || sourceY % sampleStep !== 0)
        ) {
          continue;
        }

        const darkness = 1 - brightness / 255;
        const x = offsetX + (sourceX + 0.5) * scale;
        const y = offsetY + (sourceY + 0.5) * scale;

        nextParticles.push({
          x,
          y,
          homeX: x,
          homeY: y,
          velocityX: 0,
          velocityY: 0,
          opacity: opacity * (0.12 + Math.pow(darkness, 0.72) * 0.88),
          size: particleSize * (0.72 + darkness * 0.58),
        });
      }

      particles = nextParticles;
      drawParticles();
    };

    const scheduleRebuild = () => {
      if (resizeFrame !== 0) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(rebuildParticles);
    };

    const deactivatePointer = () => {
      if (!pointer.active) return;
      pointer.active = false;
      requestAnimation();
    };

    const handlePointerMove = (event) => {
      if (
        reducedMotion ||
        !finePointer ||
        event.pointerType === "touch" ||
        particles.length === 0
      ) {
        return;
      }

      const bounds = canvas.getBoundingClientRect();
      const isInside =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;

      if (!isInside) {
        deactivatePointer();
        return;
      }

      pointer.x = event.clientX - bounds.left;
      pointer.y = event.clientY - bounds.top;
      pointer.active = true;
      requestAnimation();
    };

    const handleReducedMotionChange = (event) => {
      reducedMotion = event.matches;
      pointer.active = false;

      if (reducedMotion) {
        stopAnimation();
        resetParticles();
        drawParticles();
      }
    };

    const handleFinePointerChange = (event) => {
      finePointer = event.matches;
      if (!finePointer) deactivatePointer();
    };

    const handleCoarsePointerChange = (event) => {
      coarsePointer = event.matches;
      scheduleRebuild();
    };

    const handlePointerOut = (event) => {
      if (event.relatedTarget === null) deactivatePointer();
    };

    const handleVisibilityChange = () => {
      pointer.active = false;
      if (document.hidden) {
        stopAnimation();
        return;
      }

      resetParticles();
      drawParticles();
    };

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleRebuild);
    resizeObserver?.observe(canvas);

    window.addEventListener("resize", scheduleRebuild, { passive: true });
    window.addEventListener("scroll", deactivatePointer, { passive: true });
    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    window.addEventListener("pointerout", handlePointerOut, { passive: true });
    window.addEventListener("blur", deactivatePointer);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const removeReducedMotionListener = addMediaQueryListener(
      reducedMotionQuery,
      handleReducedMotionChange,
    );
    const removeFinePointerListener = addMediaQueryListener(
      finePointerQuery,
      handleFinePointerChange,
    );
    const removeCoarsePointerListener = addMediaQueryListener(
      coarsePointerQuery,
      handleCoarsePointerChange,
    );

    scheduleRebuild();

    return () => {
      disposed = true;
      resizeObserver?.disconnect();
      removeReducedMotionListener();
      removeFinePointerListener();
      removeCoarsePointerListener();
      window.removeEventListener("resize", scheduleRebuild);
      window.removeEventListener("scroll", deactivatePointer);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerout", handlePointerOut);
      window.removeEventListener("blur", deactivatePointer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopAnimation();
      if (resizeFrame !== 0) cancelAnimationFrame(resizeFrame);
    };
  }, [
    brightnessThreshold,
    gap,
    maxDevicePixelRatio,
    mobileBreakpoint,
    mobileGap,
    mouseRadius,
    opacity,
    particleSize,
    repulsionForce,
    returnSpeed,
    velocityDamping,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        ...style,
        pointerEvents: "none",
      }}
    />
  );
}
